import { useState, useEffect, useCallback } from 'react';

import api from 'js/api-helper';
import { error } from 'js/log';
import toQueryString from 'js/utils/to-query-string';
import { parseUrlSearchParamsToArray } from 'js/utils/filter-normalization';

import { IFilterLayout, ISearch, IFilters } from 'source/NFR.types';

interface UrlQuery {
  name: string;
  value: string;
}

// Extend IFilterLayout to allow dynamic dataId property on filters
interface FilterLayoutWithDataId extends Omit<IFilterLayout, 'filters'> {
  filters: IFilters & { dataId?: number };
}

// Extend ISearch to allow dynamic dataId property on input
interface SearchWithDataId extends Omit<ISearch, 'input'> {
  input: ISearch['input'] & { dataId?: number };
}

export type SearchQueryParameter = Record<string, string | string[]>;

type UseFetchFilteredResultsReturn<T> = [
  boolean, // isLoading
  T, // data
  (searchQueryParameter: SearchQueryParameter) => void // fetchFilteredResults
];

const useFetchFilteredResults = <T extends Record<string, unknown>>(
  initialData: T,
  endpoint: string,
  currentPage: string,
  urlQuery?: UrlQuery,
  defaultParameters?: SearchQueryParameter
): UseFetchFilteredResultsReturn<T> => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<T>(initialData);

  const handleSetData = useCallback((newData: T): void => {
    const rand = Math.random();
    const dataWithFilterLayout = newData as T & { filterLayout?: { filters?: FilterLayoutWithDataId['filters'] } };
    const dataWithSearch = newData as T & { search?: { input?: SearchWithDataId['input'] } };

    if (dataWithFilterLayout.filterLayout?.filters) {
      dataWithFilterLayout.filterLayout.filters.dataId = rand;
    }
    if (dataWithSearch.search?.input) {
      dataWithSearch.search.input.dataId = rand;
    }
    setData(newData);
  }, []);

  const fetchFilteredResults = (searchQueryParameter: SearchQueryParameter): void => {
    setIsLoading(true);

    const parametersWithDefaults: SearchQueryParameter = { ...defaultParameters, ...searchQueryParameter };
    const parameterString = toQueryString(parametersWithDefaults);

    let mergedParameters: SearchQueryParameter = parametersWithDefaults;
    if (urlQuery) {
      const queryObject: SearchQueryParameter = { [urlQuery.name]: urlQuery.value };
      mergedParameters = { ...queryObject, ...parametersWithDefaults };
    }

    mergedParameters = Object.fromEntries(
      Object.entries(mergedParameters).filter(
        ([, value]) => Array.isArray(value) || (value !== '' && value !== null && value !== undefined)
      )
    );

    let urlQueryString = '?';
    if (urlQuery) {
      urlQueryString = urlQuery.value ? `?${urlQuery.name}=${urlQuery.value}&` : '?';
    }

    const urlEndpoint = currentPage.concat(urlQueryString).concat(parameterString);

    api
      .execute<T>(endpoint, mergedParameters, 'post')
      .then(response => {
        handleSetData(response);
        window.history.pushState('', '', urlEndpoint);
        setIsLoading(false);
      })
      .catch((e: Error) => {
        if (endpoint.includes('localhost:')) {
          error(
            'Got error when requesting data. \n Have you started the mock server?\nStart mock server with "npm run static:server"\n',
            e
          );
        } else {
          error('Got error when requesting data: ', e);
        }
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // Fetch new data on back/forward browser clicks
    const handlePageChange = (): void => {
      const parsedParameters = parseUrlSearchParamsToArray(window.location.search);
      if (Array.isArray(parsedParameters.q)) {
        parsedParameters.q = parsedParameters.q[0];
      }
      const searchQueryParameter: SearchQueryParameter = { ...defaultParameters, ...parsedParameters };
      setIsLoading(true);
      api
        .execute<T>(endpoint, searchQueryParameter, 'post')
        .then(response => {
          handleSetData(response);
          setIsLoading(false);
        })
        .catch((e: Error) => {
          setIsLoading(false);
          error('Got error when requesting data: ', e);
        });
    };

    window.addEventListener('popstate', handlePageChange);
    return () => window.removeEventListener('popstate', handlePageChange);
  }, [endpoint, handleSetData, defaultParameters]);

  return [isLoading, data, fetchFilteredResults];
};

export default useFetchFilteredResults;
